import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ModuleItemType, SubmoduleItem } from 'app/types/modules';
import clsx from 'clsx';
import { ROLES_GUID } from 'core/utils/user';
import React, { useEffect, useMemo, useState } from 'react';
import type { AssetImageId } from '../../../../atoms/AssetImage/AssetImage';

type UseSubmoduleRowParams = {
  submodule: SubmoduleItem;
  isDragOverlay: boolean;
  reorderMode: boolean;
  onOpenPhoto?: (item: ModuleItemType | SubmoduleItem) => void;
};

export function useSubmoduleRow({ submodule, isDragOverlay, reorderMode, onOpenPhoto }: UseSubmoduleRowParams) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: submodule.guid,
    disabled: isDragOverlay,
  });

  const [canAddImage, setCanAddImage] = useState(false);

  useEffect(() => {
    setCanAddImage(submodule.created_by?.role_guid === ROLES_GUID.TEACHER && !reorderMode);
  }, [submodule, reorderMode]);

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging || submodule.visibility === 0 ? 0.6 : 1,
    }),
    [transform, transition, isDragging, submodule.visibility]
  );

  const submoduleName = submodule.lesson_name || '';

  const isEditorialSubmodule = submodule?.created_by?.role_guid === ROLES_GUID.EDITORIAL;
  const canOpenPhoto = !reorderMode && !isEditorialSubmodule && Boolean(onOpenPhoto);

  const shouldRenderItemActions = !reorderMode;
  const shouldRenderCreatedByInfo = !isEditorialSubmodule;

  const rootRef = isDragOverlay ? undefined : setNodeRef;
  const rootStyle = isDragOverlay ? undefined : style;
  const rootDragProps = isDragOverlay ? {} : { ...attributes, ...listeners };

  const rootClassName = clsx('submodule-row', {
    'submodule-row--dragging': isDragging,
    'submodule-row--overlay': isDragOverlay,
    'submodule-row--hidden': submodule.visibility === 0,
    'submodule-row--edit-mode': !reorderMode,
  });

  const dragHandleClassName = clsx('submodule-row__drag-handle', {
    'submodule-row__drag-handle--hidden': !reorderMode,
  });

  const imageClassName = clsx('submodule-row__image', {
    'submodule-row__image--clickable': canOpenPhoto,
  });

  const assetImageId: AssetImageId = canAddImage ? 'noModuleCoverAdd' : 'noModuleCover';

  function handleOpenPhoto(e: React.MouseEvent) {
    if (!canOpenPhoto) return;
    e.stopPropagation();
    onOpenPhoto?.(submodule);
  }

  function handleOpenPhotoKey(e: React.KeyboardEvent) {
    if (!canOpenPhoto) return;
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    e.stopPropagation();
    onOpenPhoto?.(submodule);
  }

  const imageInteractiveProps = canOpenPhoto
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: handleOpenPhoto,
        onKeyDown: handleOpenPhotoKey,
      }
    : {};

  return {
    rootRef,
    rootStyle,
    rootClassName,
    rootDragProps,
    dragHandleClassName,
    imageClassName,
    imageInteractiveProps,
    assetImageId,
    submoduleName,
    shouldRenderItemActions,
    shouldRenderCreatedByInfo,
  };
}
